import { useEffect, useState } from "react";
import {
  optimizedImg,
  isManifestHydrated,
  whenManifestReady,
  type ImagePreset,
} from "@/lib/imageUtils";

/**
 * Returns an optimized image URL that automatically upgrades to the
 * pre-built manifest variant once the manifest finishes hydrating.
 * Avoids the race where a component renders with the on-the-fly
 * /render/image/ fallback before the manifest fetch completes.
 */
export function useOptimizedImg(
  src: string | null | undefined,
  preset: ImagePreset,
  opts?: { q?: number },
): string {
  const quality = opts?.q;
  const initial = src ? optimizedImg(src, preset, quality ? { q: quality } : undefined) : "";
  const key = `${src ?? ""}|${preset}|${quality ?? ""}`;
  const [state, setState] = useState<{ key: string; url: string }>({ key, url: initial });

  // Render-phase derived state: when `src` transitions falsy -> truthy (e.g. a
  // CMS query resolving), a useState-only initial value would leave `url` as ""
  // for one commit, rendering <img src=""> and triggering React's empty-src
  // warning plus a wasted fetch of the whole page. Updating during render keeps
  // the returned URL in sync with the current src on every commit.
  if (state.key !== key) {
    setState({ key, url: initial });
  }

  useEffect(() => {
    if (!src || isManifestHydrated()) return;
    let cancelled = false;
    whenManifestReady(2000).then(() => {
      if (cancelled) return;
      const renderOpts = quality ? { q: quality } : undefined;
      const upgraded = optimizedImg(src, preset, renderOpts);
      setState((s) => (s.key === key ? { key, url: upgraded } : s));
    });
    return () => {
      cancelled = true;
    };
  }, [src, preset, quality, key]);

  return state.key === key ? state.url : initial;
}